import React from "react";
import "@/utils/axiosInterceptor"; // Must be first — global 401 handler
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import CookieConsent from "@/components/CookieConsent";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import CreateEvent from "@/pages/CreateEvent";
import EventDetail from "@/pages/EventDetail";
import Leads from "@/pages/Leads";
import Settings from "@/pages/Settings";
import PublicProfile from "@/pages/PublicProfile";
import ServiceProviders from "@/pages/ServiceProviders";
import RegisterServiceProvider from "@/pages/RegisterServiceProvider";
import ResetPassword from "@/pages/ResetPassword";
import Templates from "@/pages/Templates";
import TemplateEditor from "@/pages/TemplateEditor";
import FileUpload from "@/pages/FileUpload";
import BrokerDashboard from "@/pages/BrokerDashboard";
import BrokerRequests from "@/pages/BrokerRequests";
import BrokerEvents from "@/pages/BrokerEvents";
import BrokerProperties from "@/pages/BrokerProperties";
import PropertyDetail from "@/pages/PropertyDetail";
import AgentDetail from "@/pages/AgentDetail";
import AgentAssignments from "@/pages/AgentAssignments";
import AgentProperties from "@/pages/AgentProperties";
import AgentPropertyDetail from "@/pages/AgentPropertyDetail";
import QuickSignInSettings from "@/pages/Agent/QuickSignInSettings";
import QuickSignInPage from "@/pages/QuickSignInPage";
import QuickSignInDashboard from "@/pages/QuickSignInDashboard";
import PublicSearch from "@/pages/PublicSearch";
import PublicAgentView from "@/pages/Public/PublicAgentView";
import PublicProviderView from "@/pages/Public/PublicProviderView";
import LandingPage from "@/pages/LandingPage";
import JoinBroker from "@/pages/JoinBroker";
import VisitorSignIn from "@/pages/VisitorSignIn";
import PrivateLeadSignIn from "@/pages/PrivateLeadSignIn";
import ProviderPool from "@/pages/ProviderPool";
import AssignedLeadDashboard from "@/pages/AssignedLeadDashboard";
import AdminLogin from "@/pages/Admin/AdminLogin";
import AdminDashboard from "@/pages/Admin/AdminDashboard";
import AdminSettings from "@/pages/Admin/AdminSettings";
import AdminLogs from "@/pages/Admin/AdminLogs";
import MasterDataManagement from "@/pages/Admin/MasterDataManagement";
import BrokerDetail from "@/pages/Admin/BrokerDetail";
import AdminAgentDetail from "@/pages/Admin/AgentDetail";
import AdminProviderDetail from "@/pages/Admin/AdminProviderDetail";
import SiteSettings from "@/pages/Admin/SiteSettings";
import AdminConnectModeration from "@/pages/Admin/AdminConnectModeration";
import ContactRequests from "@/pages/Admin/ContactRequests";
import ContactPage from "@/pages/Public/ContactPage";
import AboutPage from "@/pages/Public/AboutPage";
import CareerPage from "@/pages/Public/CareerPage";
import FAQPage from "@/pages/Public/FAQPage";
import ReviewSubmissionPage from "@/pages/ReviewSubmissionPage";
import CustomPage from "@/pages/Public/CustomPage";
import AlbriksConnect from "@/pages/AlbriksConnect";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProviderAuthProvider, useProviderAuth } from "@/context/ProviderAuthContext";
import ProviderDashboard from "@/pages/Provider/Dashboard";
import ProviderLeadDetail from "@/pages/Provider/LeadDetail";
import ProviderSettings from "@/pages/Provider/Settings";
import ProviderPublicProfile from "@/pages/Provider/PublicProfile";
import ProviderReviews from "@/pages/Provider/Reviews";
import ProviderQRCodes from "@/pages/Provider/QRCodes";
import ProviderQuickSignIn from "@/pages/Provider/ProviderQuickSignIn";
import ProviderLayout from "@/pages/Provider/ProviderLayout";
import Reviews from "@/pages/Reviews";
import QRCodes from "@/pages/QRCodes";
import PostJobs from "@/pages/PostJobs";
import FindJobs from "@/pages/FindJobs";
import { ThemeProvider } from "@/context/ThemeContext";
import { NotificationProvider } from "@/context/NotificationContext";
import PrivacyPolicyPage from "@/pages/Public/PrivacyPolicyPage";
import DeleteAccountPage from "@/pages/Public/DeleteAccountPage";
import TermsPage from "@/pages/Public/TermsPage";

const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? children : <Navigate to="/" />;
};

const ProviderRoute = ({ children }) => {
  const { token } = useProviderAuth();
  return token ? children : <Navigate to="/" />;
};

const AdminRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken');
  return token ? children : <Navigate to="/" />;
};

// Smart redirect based on user role
const RoleBasedRedirect = () => {
  const { agent, loading: agentLoading } = useAuth();
  const { provider, loading: providerLoading } = useProviderAuth();
  
  // Wait for both contexts to finish loading
  if (agentLoading || providerLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }
  
  if (provider) {
    return <Navigate to="/provider/dashboard" />;
  }
  
  if (!agent) {
    return <Navigate to="/login" />;
  }
  
  if (agent.role === 'broker') {
    return <Navigate to="/broker" />;
  } else if (agent.role === 'agent') {
    return <Navigate to="/dashboard" />;
  }
  
  return <Navigate to="/dashboard" />;
};

// Smart home page - landing for guests, redirect for logged-in users
const SmartHome = () => {
  const { agent, loading: agentLoading } = useAuth();
  const { provider, loading: providerLoading } = useProviderAuth();
  
  if (agentLoading || providerLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }
  
  if (provider) return <Navigate to="/provider/dashboard" />;
  if (agent?.role === 'broker') return <Navigate to="/broker" />;
  if (agent) return <Navigate to="/dashboard" />;
  
  return <LandingPage />;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <ProviderAuthProvider>
        <NotificationProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/onboard/:regKey" element={<Register />} />
              <Route path="/agent-onboard/:agentRegKey" element={<Register />} />
              <Route path="/provider-onboard/:providerRegKey" element={<RegisterServiceProvider />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/search" element={<PublicSearch />} />
              <Route path="/agent/:agentId/view" element={<PublicAgentView />} />
              <Route path="/provider/:providerId/view" element={<PublicProviderView />} />
              <Route path="/sign-in/:eventId" element={<VisitorSignIn />} />
              <Route path="/private-signin/:agentId" element={<PrivateLeadSignIn />} />
              <Route path="/quick-signin/:agentId" element={<QuickSignInPage />} />
              <Route path="/join-broker/:brokerId" element={<JoinBroker />} />
              <Route path="/register-provider/:agentId" element={<RegisterServiceProvider />} />

              
              {/* Provider Routes - Sidebar Layout */}
              <Route
                path="/provider"
                element={
                  <ProviderRoute>
                    <ProviderLayout />
                  </ProviderRoute>
                }
              >
                <Route path="dashboard" element={<ProviderDashboard />} />
                <Route path="public-profile" element={<ProviderPublicProfile />} />
                <Route path="quick-signin" element={<ProviderQuickSignIn />} />
                <Route path="reviews" element={<ProviderReviews />} />
                <Route path="qr-codes" element={<ProviderQRCodes />} />
                <Route path="settings" element={<ProviderSettings />} />
                <Route path="post-jobs" element={<PostJobs tokenKey="providerToken" />} />
                <Route path="leads/:assignmentId" element={<ProviderLeadDetail />} />
              </Route>
              
              {/* Public Pages */}
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/career" element={<CareerPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/find-jobs" element={<FindJobs />} />
              <Route path="/review/:userType/:userId" element={<ReviewSubmissionPage />} />
              
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/privacy-policy/" element={<PrivacyPolicyPage />} />

              <Route path="/terms" element={<TermsPage />} />
              <Route path="/terms/" element={<TermsPage />} />
              
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/delete-account/" element={<DeleteAccountPage />} />
              
              <Route path="/page/:slug" element={<CustomPage />} />
              <Route path="/connect" element={<AlbriksConnect />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<Navigate to="/login" replace />} />
            <Route
              path="/admin/dashboard"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <AdminRoute>
                  <AdminSettings />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <AdminRoute>
                  <AdminLogs />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/master-data"
              element={
                <AdminRoute>
                  <MasterDataManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/site-settings"
              element={
                <AdminRoute>
                  <SiteSettings />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/contact-requests"
              element={
                <AdminRoute>
                  <ContactRequests />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/connect"
              element={
                <AdminRoute>
                  <AdminConnectModeration />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/brokers/:brokerId"
              element={
                <AdminRoute>
                  <BrokerDetail />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/agents/:agentId"
              element={
                <AdminRoute>
                  <AdminAgentDetail />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/providers/:providerId"
              element={
                <AdminRoute>
                  <AdminProviderDetail />
                </AdminRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/create"
              element={
                <ProtectedRoute>
                  <CreateEvent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:eventId"
              element={
                <ProtectedRoute>
                  <EventDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:eventId"
              element={
                <ProtectedRoute>
                  <EventDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <Leads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/public-profile"
              element={
                <ProtectedRoute>
                  <PublicProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reviews"
              element={
                <ProtectedRoute>
                  <Reviews />
                </ProtectedRoute>
              }
            />
            <Route
              path="/qr-codes"
              element={
                <ProtectedRoute>
                  <QRCodes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/service-providers"
              element={
                <ProtectedRoute>
                  <ServiceProviders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/provider-pool"
              element={
                <ProtectedRoute>
                  <ProviderPool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assigned-leads"
              element={
                <ProtectedRoute>
                  <AssignedLeadDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assigned-leads/:providerId"
              element={
                <ProtectedRoute>
                  <AssignedLeadDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assigned-leads/:providerId/:assignmentId"
              element={
                <ProtectedRoute>
                  <AssignedLeadDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/file-upload"
              element={
                <ProtectedRoute>
                  <FileUpload />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute>
                  <Templates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/create"
              element={
                <ProtectedRoute>
                  <TemplateEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/edit/:templateId"
              element={
                <ProtectedRoute>
                  <TemplateEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker"
              element={
                <ProtectedRoute>
                  <BrokerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker/requests"
              element={
                <ProtectedRoute>
                  <BrokerRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker/events"
              element={
                <ProtectedRoute>
                  <BrokerEvents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker/events/:eventId"
              element={
                <ProtectedRoute>
                  <EventDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker/properties"
              element={
                <ProtectedRoute>
                  <BrokerProperties />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker/properties/:propertyId"
              element={
                <ProtectedRoute>
                  <PropertyDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker/agents/:agentId"
              element={
                <ProtectedRoute>
                  <AgentDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/assignments"
              element={
                <ProtectedRoute>
                  <AgentAssignments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/properties"
              element={
                <ProtectedRoute>
                  <AgentProperties />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/properties/:propertyId"
              element={
                <ProtectedRoute>
                  <AgentPropertyDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/quick-signin"
              element={
                <ProtectedRoute>
                  <QuickSignInDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broker/quick-signin"
              element={
                <ProtectedRoute>
                  <QuickSignInDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/post-jobs"
              element={
                <ProtectedRoute>
                  <PostJobs tokenKey="token" useLayout={true} />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<SmartHome />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
        {/* CookieConsent removed */}
      </div>
      </NotificationProvider>
      </ProviderAuthProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
